import { GasType } from '../GasType'
import {
  GhgEmissionCalculationMaterial,
  GhgEmissionModifiedTonkiloCalcuationMaterial,
} from '../GhgEmissionCalculationMaterial'
import { ModifiedTonkiloTransportType } from '../TransportType'

// Referring to government organization (経済産業省、国土交通省) in japan
// https://www.mlit.go.jp/common/001037031.pdf
//  weight(ton) × distance(kilo meter)
//  × fuelConsumptionSpecificUnit ( litter / ton / kilo meter)
//  × 1/1000 (kilo litter / litter)
//  × unit calorific value (Giga joule / kilo litter)
//  × gas emissions coefficient (ton-Gas weight / Giga joule)
//  => gas emissions weight(ton)
// 輸送重量(t) × 輸送距離(km) × 燃料使用原単位(l/(t x km)) × 1/1000(kl / l)
//  × 単位発熱量(Gj / kl) × ガス排出量係数(t / Gj)
//  => ガス排出量(t)
export const calcModifiedTonkiloEmission = (
  ton: number,
  kilometer: number,
  fuelConsumptionSpecificUnit: number,
  unitCalorificValue: number,
  gasEmissionCoefficient: number
) => ((ton * kilometer * fuelConsumptionSpecificUnit) / 1000) * unitCalorificValue * gasEmissionCoefficient

function calcFuelConsumptionSpecificUnit(
  loadRate: number,
  maxLoadingWeight: number,
  transportType: ModifiedTonkiloTransportType
) {
  let fuelConsumptionSpecificUnit = 0
  switch (transportType) {
    case 'gasoline-light-cargo':
    case 'gasoline-lt-2t':
    case 'gasoline-gte-2t':
      // when gasoline engine
      // logn fuel consumption specific unit = 2.67 - 0.927 logn(load rate/100) - 0.648 logn(max load capacity(kg))
      //   => fuel consumption specific unit = exp(2.67 - 0.927 logn(load rate/100) - 0.648 logn(max load capacity(kg))
      // https://www.mlit.go.jp/common/001037031.pdf Page.7
      fuelConsumptionSpecificUnit = Math.exp(
        2.67 - 0.927 * Math.log(loadRate) - 0.648 * Math.log(maxLoadingWeight * 1000)
      )
      break
    case 'light-oil-lt-1t':
    case 'light-oil-gte-1t-lt-2t':
    case 'light-oil-gte-2t-lt-4t':
    case 'light-oil-gte-4t-lt-6t':
    case 'light-oil-gte-6t-lt-8t':
    case 'light-oil-gte-8t-lt-10t':
    case 'light-oil-gte-10t-lt-12t':
    case 'light-oil-gte-12t-lt-17t':
      // when diesel engine (light oil)
      // logn fuel consumption specific unit = 2.71 - 0.812 logn(load rate/100) - 0.654 logn(max load capacity(kg))
      //   => fuel consumption specific unit = exp(2.71 - 0.812 logn(load rate/100) - 0.654 logn(max load capacity(kg))
      // https://www.mlit.go.jp/common/001037031.pdf Page.7
      fuelConsumptionSpecificUnit = Math.exp(
        2.71 - 0.812 * Math.log(loadRate) - 0.654 * Math.log(maxLoadingWeight * 1000)
      )
      break
  }
  return Number(fuelConsumptionSpecificUnit.toPrecision(2))
}

function calcCo2UnitCalorificValue(transportType: ModifiedTonkiloTransportType) {
  switch (transportType) {
    case 'gasoline-light-cargo':
    case 'gasoline-lt-2t':
    case 'gasoline-gte-2t':
      // when gasoline engine
      // This value will be updated periodically. Use latest one.
      // https://www.mlit.go.jp/common/001037031.pdf Page.4
      // https://www.enecho.meti.go.jp/category/saving_and_new/saving/enterprise/transport/institution/ninushi_santeikokuji.pdf  Page.4
      // https://elaws.e-gov.go.jp/document?lawid=411CO0000000143_20230401_505CO0000000068
      return 34.6
    case 'light-oil-lt-1t':
    case 'light-oil-gte-1t-lt-2t':
    case 'light-oil-gte-2t-lt-4t':
    case 'light-oil-gte-4t-lt-6t':
    case 'light-oil-gte-6t-lt-8t':
    case 'light-oil-gte-8t-lt-10t':
    case 'light-oil-gte-10t-lt-12t':
    case 'light-oil-gte-12t-lt-17t':
      // when diesel engine (light oil)
      // This value will be updated periodically. Use latest one.
      // https://www.mlit.go.jp/common/001037031.pdf Page.4
      // https://www.enecho.meti.go.jp/category/saving_and_new/saving/enterprise/transport/institution/ninushi_santeikokuji.pdf Page.4
      // https://elaws.e-gov.go.jp/document?lawid=411CO0000000143_20230401_505CO0000000068
      return 37.7
  }
}

function calcCarbonEmissionCoefficient(transportType: ModifiedTonkiloTransportType) {
  switch (transportType) {
    case 'gasoline-light-cargo':
    case 'gasoline-lt-2t':
    case 'gasoline-gte-2t':
      // when gasoline engine
      // This value will be updated periodically. Use latest one.
      // https://www.mlit.go.jp/common/001037031.pdf Page.4
      // https://elaws.e-gov.go.jp/document?lawid=411CO0000000143_20230401_505CO0000000068
      return 0.0183
    case 'light-oil-lt-1t':
    case 'light-oil-gte-1t-lt-2t':
    case 'light-oil-gte-2t-lt-4t':
    case 'light-oil-gte-4t-lt-6t':
    case 'light-oil-gte-6t-lt-8t':
    case 'light-oil-gte-8t-lt-10t':
    case 'light-oil-gte-10t-lt-12t':
    case 'light-oil-gte-12t-lt-17t':
      // when diesel engine (light oil)
      // This value will be updated periodically. Use latest one.
      // https://www.mlit.go.jp/common/001037031.pdf Page.4
      // https://elaws.e-gov.go.jp/document?lawid=411CO0000000143_20230401_505CO0000000068
      return 0.0187
  }
}

function convertToCalcMaterial(material: unknown) {
  const {
    transportType,
    calculationType,
    gasTypes,
    weight,
    distance,
    loadRate,
    maxWeightCapacity,
  } = material as GhgEmissionModifiedTonkiloCalcuationMaterial
  return {
    calculationType,
    transportType,
    gasTypes,
    weight,
    distance,
    loadRate,
    maxWeightCapacity,
  } as GhgEmissionModifiedTonkiloCalcuationMaterial
}

export function calcModifiedTonkiloCo2Emission(material: GhgEmissionCalculationMaterial) {
  const calcMaterial = convertToCalcMaterial(material)
  const transportType = calcMaterial.transportType as ModifiedTonkiloTransportType

  // when diesel engine (light oil)
  // https://www.mlit.go.jp/common/001037031.pdf Page.4
  const fuelConsumptionSpecificUnit = calcFuelConsumptionSpecificUnit(
    calcMaterial.loadRate,
    calcMaterial.maxWeightCapacity,
    transportType
  )
  const unitCalorificValue = calcCo2UnitCalorificValue(transportType)
  // CO_2 molecular weight (44) / Carbon molecular weight (12)
  const co2EmissionCoefficient = calcCarbonEmissionCoefficient(transportType) * (44 / 12)
  return calcModifiedTonkiloEmission(
    calcMaterial.weight,
    calcMaterial.distance,
    fuelConsumptionSpecificUnit,
    unitCalorificValue,
    co2EmissionCoefficient
  )
}

export function getModifiedTonkiloEmissionMap(material: GhgEmissionCalculationMaterial) {
  const coefficientMap = new Map<GasType, number>()
  material.gasTypes.forEach((gasType) => {
    switch (gasType) {
      case 'CO_2':
        coefficientMap.set(gasType, calcModifiedTonkiloCo2Emission(material))
        break
      case 'CH_4':
      case 'N_2O':
      default:
        coefficientMap.set(gasType, 0) // not supported yet
    }
  })

  return coefficientMap
}
